import { Fragment, useState, useMemo, useRef } from 'react';
import {
  Table, TableBody, TableCell,
  TableContainer, TableHead, 
  TableRow, IconButton, TablePagination, TableSortLabel, Box, Tooltip
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import { BuzzvilDetailMetricsData } from '../../../types/buzzvil.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: BuzzvilDetailMetricsData[];
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

type DateSummary = {
  date: string;
  totalRevenue: number;
  totalImp: number;
  totalClk: number;
  unitTypes: BuzzvilDetailMetricsData[];
};

export default function BuzzvilDetailMetricsTable({ data, selectedApp, startDate, endDate }: Props) {
  const rowsPerPage = 31;
  const [openDateRows, setOpenDateRows] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(0);
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  // 날짜별로 그룹화하고 총합 계산
  const dateSummariesRaw = useMemo(() => {
    const grouped = data.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = {
          date: item.date,
          totalRevenue: 0,
          totalImp: 0,
          totalClk: 0,
          unitTypes: []
        };
      }
      acc[item.date].totalRevenue += item.revenue;
      acc[item.date].totalImp += item.imp;
      acc[item.date].totalClk += item.clk;
      acc[item.date].unitTypes.push(item);
      return acc;
    }, {} as Record<string, DateSummary>);

    return Object.values(grouped);
  }, [data]);

  const { sorted: dateSummaries, sortDirection, toggleSort } = useDateSort(dateSummariesRaw);

  // 페이지네이션된 데이터
  const paginatedSummaries = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return dateSummaries.slice(startIndex, endIndex);
  }, [dateSummaries, page, rowsPerPage]);

  const toggleDateRow = (date: string) => {
    setOpenDateRows(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const formatCurrency = (value: number) => `₩${Math.round(value).toLocaleString('ko-KR')}`;
  // 엑셀용: 통화기호 없이 숫자만
  const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');
  const formatNumber = (value: number) => Math.round(value).toLocaleString('ko-KR');
  const formatPercent = (value: number) => `${value.toFixed(2)}%`;

  // 합계 계산 (CTR만 평균, 나머지 합계)
  const totals = useMemo(() => {
    const totalRevenue = dateSummaries.reduce((sum, summary) => sum + summary.totalRevenue, 0);
    const totalImp = dateSummaries.reduce((sum, summary) => sum + summary.totalImp, 0);
    const totalClk = dateSummaries.reduce((sum, summary) => sum + summary.totalClk, 0);

    // CTR 평균 계산: 각 날짜 CTR의 산술평균 (무효/0 노출 제외)
    const ctrValues = dateSummaries
      .filter((summary) => summary.totalImp > 0 && summary.totalClk <= summary.totalImp)
      .map((summary) => (summary.totalClk / summary.totalImp) * 100);
    const avgCTR =
      ctrValues.length > 0
        ? ctrValues.reduce((sum, ctr) => sum + ctr, 0) / ctrValues.length
        : 0;

    return {
      totalRevenue,
      totalImp,
      totalClk,
      avgCTR
    };
  }, [dateSummaries]);

  // 엑셀 다운로드용 전체 데이터 (날짜별 요약만, 현재 정렬 상태 그대로)
  // 퍼센트 컬럼은 소수로 전달
  const allRowsDataForExcel = useMemo(() => {
    return dateSummaries.map((summary) => {
      const isInvalid = summary.totalClk > summary.totalImp;
      // CTR을 소수 형태로 (0.xx)
      const totalCtr = summary.totalImp > 0 && !isInvalid
        ? summary.totalClk / summary.totalImp
        : 0;

      return [
        summary.date,
        formatNumberOnly(summary.totalRevenue),
        formatNumber(summary.totalImp),
        formatNumber(summary.totalClk),
        isInvalid || summary.totalImp === 0 ? '-' : totalCtr
      ];
    });
  }, [dateSummaries]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu 
          tableRef={tableRef} 
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="버즈빌"
        />
      </Box>
      <TableContainer
      sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          overflowX: 'auto'
        }}
      >
      <Table
        ref={tableRef}
        stickyHeader
        size="small"
        sx={{
            borderCollapse: 'collapse',
            width: '100%'
        }}
      >
        <TableHead>
          <TableRow sx={{ height: 36 }}>
            <TableCell sx={{ width: 40, px: '4px' }}/>
            <TableCell sx={{ fontWeight: 'bold', px: '6px' }}>
              <TableSortLabel
                active
                direction={sortDirection}
                onClick={toggleSort}
              >
                날짜/지면
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '6px' }}>총 매출</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '6px' }}>Impression</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '6px' }}>Click</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', px: '6px' }}>CTR</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* 합계 행 */}
          <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
            <TableCell />
            <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(totals.totalRevenue)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatNumber(totals.totalImp)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {formatNumber(totals.totalClk)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {totals.avgCTR > 0 ? formatPercent(totals.avgCTR) : '-'}
                <Tooltip title="조회한 기간의 평균입니다" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
          {paginatedSummaries.map(summary => {
            const dateKey = summary.date;
            const isOpen = openDateRows[dateKey];
            const isInvalid = summary.totalClk > summary.totalImp;
            const totalCtr = summary.totalImp > 0 && !isInvalid
              ? (summary.totalClk / summary.totalImp) * 100 
              : 0;

            return (
              <Fragment key={dateKey}>
                {/* 날짜 요약 행 */}
                <TableRow 
                  sx={{ 
                    backgroundColor: '#fff', 
                    height: 32,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleDateRow(dateKey)}>
                      {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{summary.date}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(summary.totalRevenue)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(summary.totalImp)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(summary.totalClk)}
                  </TableCell>
                  <TableCell align="right">
                    {isInvalid || summary.totalImp === 0 ? '-' : formatPercent(totalCtr)}
                  </TableCell>
                </TableRow>

                {/* 지면별 상세 데이터 */}
                {isOpen && summary.unitTypes
                  .sort((a, b) => b.revenue - a.revenue) // 매출순 정렬
                  .map((unit, index) => {
                    const isUnitInvalid = unit.clk > unit.imp;
                    const unitCtr = unit.imp > 0 && !isUnitInvalid 
                      ? (unit.clk / unit.imp) * 100 
                      : 0;
                    return (
                      <TableRow 
                        key={`${dateKey}-${unit.unitType}-${index}`}
                        sx={{ 
                          backgroundColor: '#fafafa',
                          height: 32
                        }}
                      >
                        <TableCell />
                        <TableCell sx={{ pl: 4 }}>{unit.unitType}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(unit.revenue)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(unit.imp)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(unit.clk)}
                        </TableCell>
                        <TableCell align="right">
                          {isUnitInvalid || unit.imp === 0 ? '-' : formatPercent(unitCtr)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={dateSummaries.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </TableContainer>
    </Box>
  );
}
